import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronDown, ChevronRight, Users, Briefcase, Calendar, PartyPopper, UserCheck, ShoppingCart, MessageSquare, GraduationCap, Monitor, Webhook, Menu, X, FileText, Brain, LayoutGrid, Table2, FileType, Bot, CreditCard, Store, GitBranch, PenTool, Kanban, Plug, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from './ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';

// Template categories configuration - Only Form Templates (Quiz is now a category within)
const TEMPLATE_CATEGORIES = [
  { id: 'form-templates', name: 'Form Templates', icon: FileText, color: 'bg-blue-600', path: '/templates', column: 'left' },
];

// Legacy categories for the templates page filter (including Quiz as a category)
const LEGACY_TEMPLATE_CATEGORIES = [
  { id: 'contact-leads', name: 'Contact & Leads', icon: Users, color: 'bg-blue-500' },
  { id: 'business-office', name: 'Business & Office', icon: Briefcase, color: 'bg-purple-500' },
  { id: 'booking-scheduling', name: 'Booking & Scheduling', icon: Calendar, color: 'bg-green-500' },
  { id: 'events', name: 'Events', icon: PartyPopper, color: 'bg-pink-500' },
  { id: 'hr-jobs', name: 'HR & Jobs', icon: UserCheck, color: 'bg-orange-500' },
  { id: 'orders-payments', name: 'Orders & Payments', icon: ShoppingCart, color: 'bg-emerald-500' },
  { id: 'feedback-surveys', name: 'Feedback & Surveys', icon: MessageSquare, color: 'bg-cyan-500' },
  { id: 'education', name: 'Education', icon: GraduationCap, color: 'bg-indigo-500' },
  { id: 'it-technical', name: 'IT & Technical', icon: Monitor, color: 'bg-slate-500' },
  { id: 'quiz', name: 'Quiz & Assessment', icon: Brain, color: 'bg-gradient-to-r from-purple-500 to-orange-500' },
];

// Integration categories with their integrations for dynamic counts
const INTEGRATION_DATA = {
  crm: {
    name: 'CRM',
    color: 'text-blue-600',
    integrations: ['Salesforce', 'HubSpot', 'Zoho CRM', 'Pipedrive', 'Microsoft Dynamics']
  },
  storage: {
    name: 'Storage',
    color: 'text-green-600',
    integrations: ['Google Sheets', 'Microsoft Excel', 'Dropbox', 'Google Drive', 'OneDrive', 'Airtable']
  },
  payment: {
    name: 'Payment',
    color: 'text-purple-600',
    integrations: ['Stripe', 'PayPal', 'Square', 'Razorpay']
  },
  email: {
    name: 'Email',
    color: 'text-orange-600',
    integrations: ['Mailchimp', 'SendGrid', 'Mailerlite', 'ConvertKit', 'ActiveCampaign']
  },
  automations: {
    name: 'Automations',
    color: 'text-pink-600',
    integrations: ['Zapier', 'Make.com', 'n8n', 'Custom Webhook']
  },
};

// Featured integrations with Microsoft Sheet added
const FEATURED_INTEGRATIONS = [
  { id: 'microsoft_teams', name: 'Microsoft Teams', bgColor: 'bg-purple-100', category: 'crm' },
  { id: 'paypal', name: 'PayPal', bgColor: 'bg-blue-100', category: 'payment' },
  { id: 'slack', name: 'Slack', bgColor: 'bg-purple-100', category: 'crm' },
  { id: 'google_sheets', name: 'Google Sheets', bgColor: 'bg-green-100', category: 'storage' },
  { id: 'microsoft_excel', name: 'Microsoft Excel', bgColor: 'bg-green-100', category: 'storage' },
  { id: 'mailchimp', name: 'Mailchimp', bgColor: 'bg-yellow-100', category: 'email' },
  { id: 'zoom', name: 'Zoom', bgColor: 'bg-blue-100', category: 'crm' },
  { id: 'dropbox', name: 'Dropbox', bgColor: 'bg-blue-100', category: 'storage' },
  { id: 'zapier', name: 'Zapier', bgColor: 'bg-orange-100', category: 'automations' },
  { id: 'custom_webhook', name: 'Custom Webhook', bgColor: 'bg-indigo-100', isWebhook: true, category: 'automations' },
];

// Templates Dropdown Component - Simple 2-option menu
const TemplatesDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isActive = location.pathname === '/templates' || location.pathname.startsWith('/templates/');

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <Link
        to="/templates"
        className={`flex items-center gap-1 font-medium transition-colors py-4 ${
          isActive ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'
        }`}
        data-testid="templates-dropdown-trigger"
      >
        Templates
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Link>

      {isOpen && (
        <div className="absolute top-full left-0 pt-0 z-50">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-4 w-[280px]">
            {/* Header */}
            <div className="pb-3 mb-3 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Templates</p>
            </div>
            
            {/* Template Options */}
            <div className="space-y-1">
              {TEMPLATE_CATEGORIES.map(cat => {
                const Icon = cat.icon;
                const isGradient = cat.color.includes('gradient');
                return (
                  <Link
                    key={cat.id}
                    to={cat.path}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-all duration-200 group"
                    onClick={() => setIsOpen(false)}
                    data-testid={`template-${cat.id}`}
                  >
                    <div className={`w-10 h-10 ${isGradient ? cat.color : cat.color} rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-200 shadow-md`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600 transition-colors duration-200">
                      {cat.name}
                    </span>
                  </Link>
                );
              })}
            </div>
            
            {/* Footer */}
            <div className="pt-3 mt-3 border-t border-slate-100">
              <Link 
                to="/templates"
                className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors duration-200"
                onClick={() => setIsOpen(false)}
              >
                View all templates
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Simple Integrations Dropdown with Real Logos
const IntegrationsDropdownSimple = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isActive = location.pathname === '/integrations';

  const featuredIntegrations = [
    { 
      id: 'google_sheets', 
      name: 'Google Sheets',
      logo: (
        <svg viewBox="0 0 87.3 78" className="w-7 h-7">
          <path fill="#0F9D58" d="M6.8 0L0 23.5l6.8 23.5h80.5V0z"/>
          <path fill="#34A853" d="M6.8 78L0 54.5V23.5L6.8 0v78z"/>
          <path fill="#188038" d="M87.3 0v47H6.8V0z"/>
          <path fill="#FFF" d="M23.5 15.5h40.3v3H23.5zm0 9h40.3v3H23.5zm0 9h25v3h-25z"/>
        </svg>
      )
    },
    { 
      id: 'slack', 
      name: 'Slack',
      logo: (
        <svg viewBox="0 0 24 24" className="w-7 h-7">
          <path fill="#E01E5A" d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z"/>
          <path fill="#36C5F0" d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z"/>
          <path fill="#2EB67D" d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.27 0a2.528 2.528 0 0 1-2.522 2.521 2.528 2.528 0 0 1-2.521-2.521V2.522A2.528 2.528 0 0 1 15.165 0a2.528 2.528 0 0 1 2.521 2.522v6.312z"/>
          <path fill="#ECB22E" d="M15.165 18.956a2.528 2.528 0 0 1 2.521 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.521-2.522v-2.522h2.521zm0-1.27a2.527 2.527 0 0 1-2.521-2.522 2.527 2.527 0 0 1 2.521-2.521h6.313A2.528 2.528 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.521h-6.313z"/>
        </svg>
      )
    },
    { 
      id: 'zapier', 
      name: 'Zapier',
      logo: (
        <svg viewBox="0 0 24 24" className="w-7 h-7">
          <path fill="#FF4A00" d="M15.088 13.696l2.88-2.88h-4.08v-4.08l-2.88 2.88-2.88-2.88v4.08H4.048l2.88 2.88-2.88 2.88h4.08v4.08l2.88-2.88 2.88 2.88v-4.08h4.08l-2.88-2.88zM12 14.4a2.4 2.4 0 1 1 0-4.8 2.4 2.4 0 0 1 0 4.8z"/>
        </svg>
      )
    },
    { 
      id: 'mailchimp', 
      name: 'Mailchimp',
      logo: (
        <svg viewBox="0 0 24 24" className="w-7 h-7">
          <circle fill="#FFE01B" cx="12" cy="12" r="12"/>
          <path fill="#241C15" d="M15.68 13.2c-.32-.24-.64-.32-.96-.32-.24 0-.48.08-.64.24-.16.16-.24.32-.24.56 0 .24.08.4.24.56.16.16.4.24.64.24.32 0 .64-.08.96-.32v.88c-.32.16-.72.32-1.12.32-.48 0-.88-.16-1.2-.48-.32-.32-.48-.72-.48-1.2s.16-.88.48-1.2c.32-.32.72-.48 1.2-.48.4 0 .8.08 1.12.32v.88zm-4.4-1.6h.96v3.2h-.96v-3.2zm-1.6 1.2c0-.64-.48-1.2-1.2-1.2-.64 0-1.2.48-1.2 1.2v2h.96v-1.92c0-.24.16-.4.4-.4s.4.16.4.4v1.92h.96v-2h-.32z"/>
        </svg>
      )
    },
    { 
      id: 'webhooks', 
      name: 'Webhooks',
      logo: (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
          <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#6366F1"/>
          <path d="M2 17l10 5 10-5" stroke="#6366F1" strokeWidth="2" fill="none"/>
          <path d="M2 12l10 5 10-5" stroke="#6366F1" strokeWidth="2" fill="none"/>
        </svg>
      )
    },
  ];

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        className={`flex items-center gap-1 font-medium transition-colors ${
          isActive ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'
        }`}
        data-testid="integrations-dropdown-trigger"
      >
        Integrations
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-5">
            <div className="flex items-center gap-8">
              {featuredIntegrations.map(integration => (
                <Link
                  key={integration.id}
                  to={`/integrations?highlight=${integration.id}`}
                  className="flex flex-col items-center gap-3 group"
                  onClick={() => setIsOpen(false)}
                >
                  <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center group-hover:bg-slate-100 group-hover:scale-105 transition-all">
                    {integration.logo}
                  </div>
                  <span className="text-xs font-medium text-slate-500 group-hover:text-indigo-600">{integration.name}</span>
                </Link>
              ))}
              <Link
                to="/integrations"
                className="flex flex-col items-center gap-3 group"
                onClick={() => setIsOpen(false)}
              >
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 group-hover:scale-105 transition-all">
                  <ChevronRight className="w-6 h-6 text-indigo-600" />
                </div>
                <span className="text-xs font-medium text-indigo-600">View All</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Integrations Dropdown Component - Fixed hover and dynamic counts
const IntegrationsDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const location = useLocation();
  const isActive = location.pathname === '/integrations';

  // Get dynamic category list with counts
  const INTEGRATION_CATEGORIES = Object.entries(INTEGRATION_DATA).map(([id, data]) => ({
    id,
    name: data.name,
    color: data.color,
    count: data.integrations.length
  }));

  // Filter featured integrations by hovered category
  const filteredIntegrations = hoveredCategory 
    ? FEATURED_INTEGRATIONS.filter(int => int.category === hoveredCategory)
    : FEATURED_INTEGRATIONS;

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <Link
        to="/integrations"
        className={`flex items-center gap-1 font-medium transition-colors py-4 ${
          isActive ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'
        }`}
        data-testid="integrations-dropdown-trigger"
      >
        Integrations
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Link>

      {isOpen && (
        <div className="absolute top-full left-0 pt-0 z-50">
          <div 
            className="w-[600px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
            data-testid="integrations-dropdown-menu"
          >
            <div className="flex">
              {/* Left Sidebar - Categories with dynamic counts */}
              <div className="w-48 bg-slate-50 border-r border-slate-200 py-4">
                <h3 className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Integrations
                </h3>
                <nav className="space-y-1">
                  {INTEGRATION_CATEGORIES.map(category => (
                    <button
                      key={category.id}
                      className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors ${
                        hoveredCategory === category.id 
                          ? 'bg-white text-indigo-600 font-medium' 
                          : 'text-slate-700 hover:bg-white hover:text-indigo-600'
                      }`}
                      onMouseEnter={() => setHoveredCategory(category.id)}
                    >
                      <span>{category.name}</span>
                      <span className="flex items-center gap-1">
                        <span className="text-xs text-slate-400">{category.count}</span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </span>
                    </button>
                  ))}
                </nav>
                <div className="mt-4 px-4">
                  <Link 
                    to="/integrations"
                    className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    See all integrations
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Right Side - Featured Integrations */}
              <div className="flex-1 p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  {hoveredCategory ? INTEGRATION_DATA[hoveredCategory]?.name + ' Integrations' : 'Featured Integrations'}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {filteredIntegrations.slice(0, 10).map(integration => (
                    <Link
                      key={integration.id}
                      to="/integrations"
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                      onClick={() => setIsOpen(false)}
                    >
                      <div className={`w-10 h-10 rounded-lg ${integration.bgColor} flex items-center justify-center`}>
                        {integration.isWebhook ? (
                          <Webhook className="w-5 h-5 text-indigo-600" />
                        ) : (
                          <span className="text-lg font-semibold text-slate-600">
                            {integration.name.charAt(0)}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600 transition-colors">
                        {integration.name}
                      </span>
                    </Link>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <Link 
                    to="/integrations"
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    See more integrations →
                  </Link>
                </div>
              </div>
            </div>

            {/* Bottom Banner - Webhook Highlight */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-t border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                    <Webhook className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Custom Webhook Integration</p>
                    <p className="text-sm text-slate-600">Connect to Zapier, Make.com, n8n, or any system</p>
                  </div>
                </div>
                <Link 
                  to="/integrations"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Combined Resources Dropdown (Templates + Integrations)
const ResourcesDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('templates');
  const location = useLocation();
  const isActive = location.pathname.includes('/templates') || location.pathname.includes('/integrations');

  // Get dynamic category lists
  const INTEGRATION_CATEGORIES = Object.entries(INTEGRATION_DATA).map(([id, data]) => ({
    id,
    name: data.name,
    color: data.color,
    count: data.integrations.length
  }));

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        className={`flex items-center gap-1 font-medium transition-colors py-4 ${
          isActive ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'
        }`}
        data-testid="resources-dropdown-trigger"
      >
        Resources
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-0 z-50">
          <div 
            className="w-[700px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
            data-testid="resources-dropdown-menu"
          >
            {/* Tab Header */}
            <div className="flex border-b border-slate-200">
              <button
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'templates' 
                    ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' 
                    : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-50'
                }`}
                onMouseEnter={() => setActiveTab('templates')}
              >
                <div className="flex items-center justify-center gap-2">
                  <LayoutGrid className="w-4 h-4" />
                  Templates
                </div>
              </button>
              <button
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'integrations' 
                    ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' 
                    : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-50'
                }`}
                onMouseEnter={() => setActiveTab('integrations')}
              >
                <div className="flex items-center justify-center gap-2">
                  <Plug className="w-4 h-4" />
                  Integrations
                </div>
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'templates' && (
                <div>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {LEGACY_TEMPLATE_CATEGORIES.slice(0, 6).map(cat => {
                      const Icon = cat.icon;
                      return (
                        <Link
                          key={cat.id}
                          to={`/templates?category=${cat.id}`}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                          onClick={() => setIsOpen(false)}
                        >
                          <div className={`w-10 h-10 rounded-lg ${cat.color} flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 group-hover:text-indigo-600">{cat.name}</p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                  <div className="pt-4 border-t border-slate-100 flex justify-between items-center">
                    <span className="text-sm text-slate-500">Explore all form templates</span>
                    <Link 
                      to="/templates"
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                      onClick={() => setIsOpen(false)}
                    >
                      View all templates
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              )}

              {activeTab === 'integrations' && (
                <div>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {INTEGRATION_CATEGORIES.map(category => (
                      <Link
                        key={category.id}
                        to={`/integrations?category=${category.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                        onClick={() => setIsOpen(false)}
                      >
                        <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center`}>
                          <Plug className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 group-hover:text-indigo-600">{category.name}</p>
                          <p className="text-xs text-slate-500">{category.count} integrations</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div className="pt-4 border-t border-slate-100 flex justify-between items-center">
                    <span className="text-sm text-slate-500">Connect your favorite tools</span>
                    <Link 
                      to="/integrations"
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                      onClick={() => setIsOpen(false)}
                    >
                      View all integrations
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Mobile Menu Component with Accordion Submenus
const MobileMenuComponent = ({ isAuthenticated }) => {
  const location = useLocation();
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/about', label: 'About' },
    { path: '/features', label: 'Features' },
  ];

  const bottomNavLinks = [
    { path: '/pricing', label: 'Pricing' },
    { path: '/contact', label: 'Contact' },
  ];

  // Get dynamic category counts
  const INTEGRATION_CATEGORIES = Object.entries(INTEGRATION_DATA).map(([id, data]) => ({
    id,
    name: data.name,
    count: data.integrations.length
  }));

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
          aria-label="Open menu"
          data-testid="mobile-menu-button"
        >
          <Menu className="w-6 h-6 text-slate-900" />
        </button>
      </SheetTrigger>
      
      <SheetContent side="left" className="w-[300px] p-0 bg-white overflow-y-auto" data-testid="mobile-menu-drawer">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b border-slate-100">
          <SheetTitle className="flex items-center space-x-2">
            <img src="/formlio_final_logo.png" alt="Formlio" className="h-8 w-8 object-contain rounded-md" />
            <span className="text-xl font-bold text-slate-900">Formlio</span>
          </SheetTitle>
        </SheetHeader>

        {/* Navigation Links */}
        <nav className="flex flex-col py-4">
          {navLinks.map((item) => (
            <SheetClose asChild key={item.path}>
              <Link
                to={item.path}
                className={`px-6 py-3 text-base transition-colors ${
                  isActive(item.path)
                    ? 'text-indigo-600 font-medium bg-indigo-50'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`mobile-menu-${item.label.toLowerCase()}`}
              >
                {item.label}
              </Link>
            </SheetClose>
          ))}

          {/* Products Accordion */}
          <Collapsible open={productsOpen} onOpenChange={setProductsOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full px-6 py-3 text-base text-slate-700 hover:bg-slate-50 transition-colors">
              <span>Products</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent className="bg-slate-50">
              <SheetClose asChild>
                <Link
                  to="/features"
                  className="flex items-center gap-3 px-8 py-2.5 text-sm text-slate-600 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
                >
                  <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                    <FileText className="w-3 h-3 text-white" />
                  </div>
                  <span>Form Builder</span>
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/formlio-sign"
                  className="flex items-center gap-3 px-8 py-2.5 text-sm text-slate-600 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
                >
                  <div className="w-6 h-6 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-full flex items-center justify-center">
                    <PenTool className="w-3 h-3 text-white" />
                  </div>
                  <span>Formlio Sign</span>
                </Link>
              </SheetClose>
            </CollapsibleContent>
          </Collapsible>

          {/* Templates Accordion */}
          <Collapsible open={templatesOpen} onOpenChange={setTemplatesOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full px-6 py-3 text-base text-slate-700 hover:bg-slate-50 transition-colors">
              <span className={location.pathname.includes('/templates') ? 'text-indigo-600 font-medium' : ''}>Templates</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${templatesOpen ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent className="bg-slate-50">
              {TEMPLATE_CATEGORIES.map(cat => {
                const Icon = cat.icon;
                const isGradient = cat.color.includes('gradient');
                return (
                  <SheetClose asChild key={cat.id}>
                    <Link
                      to={cat.path}
                      className="flex items-center gap-3 px-8 py-2.5 text-sm text-slate-600 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
                    >
                      <div className={`w-6 h-6 ${isGradient ? cat.color : cat.color} rounded-full flex items-center justify-center`}>
                        <Icon className="w-3 h-3 text-white" />
                      </div>
                      <span>{cat.name}</span>
                    </Link>
                  </SheetClose>
                );
              })}
              <SheetClose asChild>
                <Link
                  to="/templates"
                  className="flex items-center gap-2 px-8 py-2.5 text-sm text-indigo-600 font-medium hover:bg-slate-100 transition-colors"
                >
                  View all templates
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </SheetClose>
            </CollapsibleContent>
          </Collapsible>

          {/* Integrations Accordion */}
          <Collapsible open={integrationsOpen} onOpenChange={setIntegrationsOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full px-6 py-3 text-base text-slate-700 hover:bg-slate-50 transition-colors">
              <span className={isActive('/integrations') ? 'text-indigo-600 font-medium' : ''}>Integrations</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${integrationsOpen ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent className="bg-slate-50">
              {INTEGRATION_CATEGORIES.map(category => (
                <SheetClose asChild key={category.id}>
                  <Link
                    to="/integrations"
                    className="flex items-center justify-between px-8 py-2.5 text-sm text-slate-600 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
                  >
                    <span>{category.name}</span>
                    <span className="text-xs text-slate-400 bg-slate-200 px-2 py-0.5 rounded-full">{category.count}</span>
                  </Link>
                </SheetClose>
              ))}
              <SheetClose asChild>
                <Link
                  to="/integrations"
                  className="flex items-center gap-3 px-8 py-2.5 text-sm text-slate-600 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
                >
                  <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                    <Webhook className="w-3 h-3 text-white" />
                  </div>
                  <span>Custom Webhook</span>
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/integrations"
                  className="flex items-center gap-2 px-8 py-2.5 text-sm text-indigo-600 font-medium hover:bg-slate-100 transition-colors"
                >
                  View all integrations
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </SheetClose>
            </CollapsibleContent>
          </Collapsible>

          {bottomNavLinks.map((item) => (
            <SheetClose asChild key={item.path}>
              <Link
                to={item.path}
                className={`px-6 py-3 text-base transition-colors ${
                  isActive(item.path)
                    ? 'text-indigo-600 font-medium bg-indigo-50'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {item.label}
              </Link>
            </SheetClose>
          ))}
        </nav>

        {/* Auth Buttons */}
        <div className="px-6 py-6 border-t border-slate-100 bg-white mt-auto">
          {isAuthenticated ? (
            <SheetClose asChild>
              <Button asChild className="w-full bg-indigo-600 hover:bg-indigo-700">
                <Link to="/dashboard">Go to Dashboard</Link>
              </Button>
            </SheetClose>
          ) : (
            <div className="flex flex-col space-y-3">
              <SheetClose asChild>
                <Button asChild className="w-full bg-indigo-600 hover:bg-[#F59E0B] text-white">
                  <Link to="/signup" data-testid="mobile-menu-signup">Sign Up Free</Link>
                </Button>
              </SheetClose>
              <SheetClose asChild>
                <Button asChild variant="outline" className="w-full hover:bg-[#F59E0B] hover:text-white hover:border-[#F59E0B]">
                  <Link to="/login" data-testid="mobile-menu-login">Login</Link>
                </Button>
              </SheetClose>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Products Dropdown Component
const ProductsDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isActive = location.pathname === '/dashboard' || location.pathname === '/verify';

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        className={`flex items-center gap-1 font-medium transition-colors py-4 ${
          isActive ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'
        }`}
        data-testid="products-dropdown-trigger"
      >
        Products
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 pt-0 z-50">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-4 w-[300px]" data-testid="products-dropdown-menu">
            <div className="pb-3 mb-3 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Products</p>
            </div>
            <div className="space-y-1">
              <Link
                to="/features"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-all duration-200 group"
                onClick={() => setIsOpen(false)}
                data-testid="product-form-builder"
              >
                <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-200 shadow-md">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600 transition-colors duration-200 block">
                    Form Builder
                  </span>
                  <span className="text-xs text-slate-500">Build forms, surveys & quizzes</span>
                </div>
              </Link>
              <Link
                to="/formlio-sign"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-all duration-200 group"
                onClick={() => setIsOpen(false)}
                data-testid="product-formlio-sign"
              >
                <div className="w-10 h-10 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-200 shadow-md">
                  <PenTool className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600 transition-colors duration-200 block">
                    Formlio Sign
                  </span>
                  <span className="text-xs text-slate-500">E-signatures & document verification</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Header Component
export const PublicHeader = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/formlio_final_logo.png" alt="Formlio" className="h-8 w-8 object-contain rounded-md" />
            <span className="text-xl font-bold text-slate-900">Formlio</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <Link 
              to="/about" 
              className={`font-medium transition-colors ${isActive('/about') ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'}`}
            >
              About
            </Link>
            <Link 
              to="/features" 
              className={`font-medium transition-colors ${isActive('/features') ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'}`}
            >
              Features
            </Link>
            <ProductsDropdown />
            <Link 
              to="/templates" 
              className={`font-medium transition-colors ${isActive('/templates') ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'}`}
            >
              Templates
            </Link>
            <Link 
              to="/integrations" 
              className={`font-medium transition-colors ${isActive('/integrations') ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'}`}
            >
              Integrations
            </Link>
            <Link 
              to="/pricing" 
              className={`font-medium transition-colors ${isActive('/pricing') ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'}`}
            >
              Pricing
            </Link>
            <Link 
              to="/contact" 
              className={`font-medium transition-colors ${isActive('/contact') ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'}`}
            >
              Contact
            </Link>
          </nav>

          {/* Auth Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            {isAuthenticated ? (
              <Button asChild className="bg-indigo-600 hover:bg-[#F59E0B]">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" asChild className="hover:bg-[#F59E0B] hover:text-white">
                  <Link to="/login">Login</Link>
                </Button>
                <Button asChild className="bg-indigo-600 hover:bg-[#F59E0B] text-white">
                  <Link to="/signup">Sign Up Free</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <MobileMenuComponent isAuthenticated={isAuthenticated} />
        </div>
      </div>
    </header>
  );
};

export default PublicHeader;
